import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  OnInit,
  OnDestroy,
  Output, ChangeDetectorRef
} from "@angular/core";
import {PANEL_DATA} from "../../../../commons/angular/services/panel.service";
import * as moment from 'moment-timezone';
import {VisitScheduleResourceModel} from "../../../models/visit-schedule-resource.model";
import {VisitPlanModel} from "../../../models/visit-plan.model";
import {VisitPlanHttpService} from "../../services/visit-plan-http.service";
import {includes, cloneDeep, map} from "lodash";
import {ResourceSkill} from "../../../../agents/types/resourceSkill.type";
import {VisitScheduleResourceHolderExt} from "../../../models/visit-schedule-resource-holder-ext.model";
import {VisitPlanRouteModel} from "../../../models/visit-plan-route.model";
import {Address} from "../../../../addresses/models/address.model";
import {ServicePointModel} from "../../../../service-points/angular/models/service-point.model";
import {getServicePointLabel} from "../../../../service-points/angular/utils/service-point-display.util";
import {
  AvailabilityCustomAddresses,
  AvailabilityLocationPoint,
  AvailabilityLocationView,
  buildAvailabilityLocationViews,
  buildDefaultLocationSettings,
  clearLocationServicePoint,
  ensureLocationSettings,
  hasIncompleteCustomAddress,
  hasLocationSettings,
  normalizeLocationSettingsForUi,
  setLocationCustomAddress,
  setLocationPointType,
  setLocationServicePoint,
  toServerLocationSettings,
  UI_CUSTOM_ADDRESS_TYPE,
  UI_SERVICE_POINT_TYPE
} from "../../utils/availability-location-settings.util";

interface DialogData {
  visitPlan: VisitPlanModel;
  availabilities: VisitScheduleResourceModel[];
  date?: string;
  zoneId?: string;
  isCreate?: boolean;
  agent?: VisitScheduleResourceHolderExt;
}

interface EndPickerBounds {
  endDayStart: number;
  endDayEnd: number;
  isSameDay: boolean;
  endMinDate: number;
  endMaxDate: number;
}

const MAX_AVAILABILITY_DAY_SPAN = 1;
const CUSTOM_ADDRESS_ERROR = 'Select a custom address from the suggestions';

require('./edit-availability-dialog.scss');
@Component({
  standalone: false,
  selector: 'app-edit-availability-dialog',
  templateUrl: './edit-availability-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditAvailabilityDialogComponent implements OnInit, OnDestroy {

  @Output() onClose = new EventEmitter<any>();

  public visitPlan: VisitPlanModel;
  public availabilities: VisitScheduleResourceModel[];
  public siteSkills: string[] = [];
  public skillsOptions: string[];
  public selectedSkillsForAvail: string[][] = [];
  public isCreate: boolean = false;
  public loading: boolean = false;
  public errorMessage: string = '';

  public dayStartTime: number = 0;
  public dayEndTime: number = 0;
  public bounds: EndPickerBounds[] = [];

  public agentAddress: Address;
  public locationViews: AvailabilityLocationView[] = [];

  public dayRoutes: VisitPlanRouteModel[] = [];
  public loadingRoutes: boolean = false;
  public selectedRouteId: string;

  private savedLocationSettingsForAvail: VisitScheduleResourceModel['locationSettings'][] = [];
  private selectedStartServicePointPlaces: string[] = [];
  private selectedEndServicePointPlaces: string[] = [];
  private customAddressesForAvail: AvailabilityCustomAddresses[] = [];
  private projectFromDate: number = 0;
  private projectToDate: number = 0;
  private previousDefaultTimezone: string | null = null;

  constructor(
    @Inject(PANEL_DATA) public data: DialogData,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject(VisitPlanHttpService) private visitPlanHttpService: VisitPlanHttpService
  ) {
  }

  ngOnInit() {
    this.visitPlan = {...this.data.visitPlan};
    this.availabilities = cloneDeep(this.data.availabilities || []);
    this.isCreate = !!this.data.isCreate;
    this.agentAddress = this.data.agent?.address ? cloneDeep(this.data.agent.address) : undefined;

    const tz = this.data?.zoneId;

    if (tz) {
      this.previousDefaultTimezone = moment.tz.guess();
      moment.tz.setDefault(tz);
    }

    const baseDate = this.data?.date ? moment(this.data.date) : moment();
    this.dayStartTime = baseDate.clone().startOf('day').valueOf();
    this.dayEndTime = baseDate.clone().endOf('day').valueOf();

    this.projectFromDate = this.visitPlan?.from
      ? moment(this.visitPlan.from).startOf('day').valueOf()
      : this.dayStartTime;
    this.projectToDate = this.visitPlan?.to
      ? moment(this.visitPlan.to).endOf('day').valueOf()
      : this.dayEndTime;

    this._recalcAllBounds();
    this._initLocationSettings();
    this._getSkills();

    if (this.isCreate) {
      this._getDayRoutes();
    }
  }

  ngOnDestroy() {
    if (this.previousDefaultTimezone) {
      moment.tz.setDefault(this.previousDefaultTimezone);
    }
  }

  setAvailabilityStatus(avail: VisitScheduleResourceModel, isAvailable: boolean) {
    if (isAvailable && avail.disabled) {
      avail.disabled = false;
    } else if (!isAvailable && !avail.disabled) {
      avail.disabled = true;
    }
    this.cdr.detectChanges();
  }

  onStartTimeChange(avail: VisitScheduleResourceModel, timestamp: number) {
    this.errorMessage = '';
    avail.startTime = timestamp;
    if (avail.endTime && avail.endTime <= avail.startTime) {
      const newToTime = moment(avail.startTime).add(1, 'hour');
      const endOfDay = moment(avail.startTime).endOf('day');
      avail.endTime = newToTime.isAfter(endOfDay) ? endOfDay.valueOf() : newToTime.valueOf();
    }
    this._recalcBoundsFor(this._indexOf(avail));
    this.validateTimeRanges();
  }

  onEndTimeChange(avail: VisitScheduleResourceModel, timestamp: number) {
    this.errorMessage = '';
    avail.endTime = timestamp;
    this._recalcBoundsFor(this._indexOf(avail));
    this.validateTimeRanges();
  }

  onEndDateChange(avail: VisitScheduleResourceModel, dateValue: number) {
    if (!dateValue) {
      return;
    }
    this.errorMessage = '';

    const newDay = moment(dateValue);
    const baseTime = avail.endTime || avail.startTime || this.dayStartTime;
    const time = moment(baseTime);

    avail.endTime = newDay
      .set({
        hour: time.hour(),
        minute: time.minute(),
        second: 0,
        millisecond: 0
      })
      .valueOf();

    if (avail.startTime && avail.endTime <= avail.startTime) {
      const newEnd = moment(avail.startTime).add(1, 'hour');
      const endOfNewEndDay = moment(avail.endTime).endOf('day');
      avail.endTime = newEnd.isAfter(endOfNewEndDay) ? endOfNewEndDay.valueOf() : newEnd.valueOf();
    }

    this._recalcBoundsFor(this._indexOf(avail));
    this.validateTimeRanges();
  }

  onAddStartEndPoint(avail: VisitScheduleResourceModel, index: number) {
    if (!avail.locationSettings) {
      if (this.savedLocationSettingsForAvail[index]) {
        avail.locationSettings = cloneDeep(this.savedLocationSettingsForAvail[index]);
        normalizeLocationSettingsForUi(avail.locationSettings, this.agentAddress);
      } else {
        avail.locationSettings = buildDefaultLocationSettings(this.agentAddress);
      }
    }
    this._refreshLocationViews();
    this._setLocationSectionVisible(index, true);
    this.cdr.detectChanges();
  }

  onPointTypeChange(
    avail: VisitScheduleResourceModel,
    index: number,
    point: AvailabilityLocationPoint,
    type: string
  ) {
    const settings = ensureLocationSettings(avail.locationSettings, this.agentAddress);
    avail.locationSettings = settings;
    setLocationPointType(settings, point, type, this.agentAddress, this.customAddressesForAvail[index]?.[point]);

    if (type !== UI_SERVICE_POINT_TYPE) {
      this._setSelectedServicePointPlace(index, point, '');
    }

    this._clearCustomAddressError();
    this._refreshLocationViews();
    this.cdr.detectChanges();
  }

  onCustomAddressChange(
    avail: VisitScheduleResourceModel,
    index: number,
    point: AvailabilityLocationPoint,
    address: Address | undefined
  ) {
    const settings = ensureLocationSettings(avail.locationSettings, this.agentAddress);
    avail.locationSettings = settings;
    setLocationCustomAddress(settings, point, address);
    this.customAddressesForAvail[index] = {
      ...this.customAddressesForAvail[index],
      [point]: address
    };

    this._clearCustomAddressError();
    this._refreshLocationViews();
    this.cdr.detectChanges();
  }

  onServicePointChange(
    avail: VisitScheduleResourceModel,
    index: number,
    point: AvailabilityLocationPoint,
    servicePoint: ServicePointModel | null
  ) {
    if (!servicePoint) {
      if (avail.locationSettings) {
        clearLocationServicePoint(avail.locationSettings, point);
      }
      this._setSelectedServicePointPlace(index, point, '');
      this._refreshLocationViews();
      this.cdr.detectChanges();
      return;
    }

    const settings = ensureLocationSettings(avail.locationSettings, this.agentAddress);
    avail.locationSettings = settings;
    setLocationServicePoint(settings, point, servicePoint);
    this._setSelectedServicePointPlace(index, point, getServicePointLabel(servicePoint));
    this._refreshLocationViews();
    this.cdr.detectChanges();
  }

  private _indexOf(avail: VisitScheduleResourceModel): number {
    return this.availabilities.indexOf(avail);
  }

  private _recalcAllBounds() {
    this.bounds = this.availabilities.map((_a, i) => this._computeBounds(i));
  }

  private _recalcBoundsFor(index: number) {
    if (index < 0) {
      return;
    }
    this.bounds[index] = this._computeBounds(index);
  }

  private _computeBounds(index: number): EndPickerBounds {
    const avail = this.availabilities[index];
    const endRef = avail?.endTime || avail?.startTime || this.dayStartTime;
    const endDayStart = moment(endRef).startOf('day').valueOf();
    const endDayEnd = moment(endRef).endOf('day').valueOf();
    const isSameDay = avail?.startTime && avail?.endTime
      ? moment(avail.startTime).isSame(moment(avail.endTime), 'day')
      : true;

    const anchorDay = avail?.startTime
      ? moment(avail.startTime).startOf('day').valueOf()
      : this.dayStartTime;
    const nextDayEnd = moment(anchorDay).add(MAX_AVAILABILITY_DAY_SPAN, 'day').endOf('day').valueOf();

    let endMinDate = Math.max(anchorDay, this.projectFromDate);
    let endMaxDate = Math.min(nextDayEnd, this.projectToDate);

    // Allow the picker to render an existing end date that falls outside the
    // current rule (legacy data created before the 2-day cap was enforced).
    if (avail?.endTime) {
      endMinDate = Math.min(endMinDate, moment(avail.endTime).startOf('day').valueOf());
      endMaxDate = Math.max(endMaxDate, moment(avail.endTime).endOf('day').valueOf());
    }

    return { endDayStart, endDayEnd, isSameDay, endMinDate, endMaxDate };
  }

  private validateTimeRanges(): boolean {
    for (const avail of this.availabilities) {
      if (!avail.startTime || !avail.endTime) {
        this.errorMessage = 'Both start and end times are required for all availabilities';
        return false;
      }
      if (avail.endTime <= avail.startTime) {
        this.errorMessage = 'End time must be after start time';
        return false;
      }
      if (avail.startTime < this.projectFromDate || avail.endTime > this.projectToDate) {
        this.errorMessage = 'Availability must be within the project range';
        return false;
      }
      const dayDelta = moment(avail.endTime).startOf('day').diff(moment(avail.startTime).startOf('day'), 'days');
      if (dayDelta > MAX_AVAILABILITY_DAY_SPAN) {
        this.errorMessage = 'Availability cannot span more than 2 days';
        return false;
      }
    }
    this.errorMessage = '';
    return true;
  }

  onBranchesSelectFor(avail: VisitScheduleResourceModel, selectedIds: string[]) {
    avail.branchIds = selectedIds;
    this.cdr.detectChanges();
  }

  onSkillsSelectFor(avail: VisitScheduleResourceModel, index: number, skills: string[]) {
    this.selectedSkillsForAvail[index] = skills;
    const skillsToSelect = this.siteSkills.filter((ss: any) => {
      return includes(skills, ss.name);
    });
    const allocationIds = map(skillsToSelect, 'id');
    avail.allocationSkills = allocationIds;
    this.cdr.detectChanges();
  }

  onCancel() {
    this.onClose.emit();
  }

  onSubmit() {
    if (!this.validateTimeRanges() || !this._validateCustomAddresses()) {
      return;
    }
    const result = this.availabilities.map((avail, index) => {
      const view = this.locationViews[index];
      if (!view?.showSection) {
        if (this.savedLocationSettingsForAvail[index]) {
          return {...avail, locationSettings: toServerLocationSettings(this.savedLocationSettingsForAvail[index])};
        }
        return {...avail, locationSettings: undefined};
      }
      return {...avail, locationSettings: toServerLocationSettings(avail.locationSettings)};
    });
    const selectedRoute = this.dayRoutes.find(r => r.id === this.selectedRouteId);
    this.onClose.emit({availabilities: result, selectedRoute});
  }

  isRouteSelectable(route: VisitPlanRouteModel): boolean {
    return route.status !== 'DISPATCHED' && !route.locked && !route.dispatchedRouteId;
  }

  onSelectRoute(routeId: string) {
    this.selectedRouteId = this.selectedRouteId === routeId ? undefined : routeId;
    this.cdr.detectChanges();
  }

  private _getDayRoutes() {
    this.loadingRoutes = true;
    const query = {
      from: this.dayStartTime,
      to: this.dayEndTime,
      projectBranchIds: this.data.visitPlan.branchIds
    };
    this.visitPlanHttpService.getVisitScheduleRoutesCursor(this.data.visitPlan.id, this.data.visitPlan.scheduleId, query)
      .subscribe({
        next: (res) => {
          const routes: VisitPlanRouteModel[] = res?.list || [];
          this.dayRoutes = routes.filter(route => route.resource?.resourceHolderId === this.data.agent?.id);
          this.loadingRoutes = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loadingRoutes = false;
          this.cdr.detectChanges();
        }
      });
  }

  private _getSkills() {
    this.loading = true;
    this.visitPlanHttpService.getSkills().subscribe((skills) => {
      this.siteSkills = skills || [];
      this.skillsOptions = map(skills.filter((s) => s.id !== ResourceSkill.Delivery && s.id !== ResourceSkill.AutonomousAgent), 'name');

      this.selectedSkillsForAvail = this.availabilities.map(avail => {
        if (!avail.allocationSkills) return [];
        const selectedSiteSkills = avail.allocationSkills.map(sId => this.siteSkills.find((ss:any) => ss.id === sId));
        return map(selectedSiteSkills, 'name').filter(Boolean) as string[];
      });

      this.loading = false;
      this.cdr.detectChanges();
    }, (error) => {
      this.loading = false;
      this.cdr.detectChanges();
    })
  }

  private _initLocationSettings() {
    this.savedLocationSettingsForAvail = this.availabilities.map(avail => {
      if (hasLocationSettings(avail.locationSettings)) {
        return cloneDeep(avail.locationSettings);
      }
      return undefined;
    });

    this.selectedStartServicePointPlaces = this.availabilities.map(() => '');
    this.selectedEndServicePointPlaces = this.availabilities.map(() => '');

    this.availabilities.forEach((avail, index) => {
      if (this.savedLocationSettingsForAvail[index]) {
        avail.locationSettings = cloneDeep(this.savedLocationSettingsForAvail[index]);
        normalizeLocationSettingsForUi(avail.locationSettings, this.agentAddress);
      } else {
        avail.locationSettings = undefined;
      }
    });

    this.customAddressesForAvail = this.availabilities.map(avail => ({
      departure: avail.locationSettings?.departurePointType === UI_CUSTOM_ADDRESS_TYPE
        ? cloneDeep(avail.locationSettings.departurePoint)
        : undefined,
      destination: avail.locationSettings?.destinationPointType === UI_CUSTOM_ADDRESS_TYPE
        ? cloneDeep(avail.locationSettings.destinationPoint)
        : undefined
    }));

    this._refreshLocationViews();
  }

  private _validateCustomAddresses(): boolean {
    const hasIncomplete = this.availabilities.some((avail, index) => {
      if (!this.locationViews[index]?.showSection) {
        return false;
      }
      const initialSettings = cloneDeep(this.savedLocationSettingsForAvail[index]);
      if (initialSettings) {
        normalizeLocationSettingsForUi(initialSettings, this.agentAddress);
      }
      return hasIncompleteCustomAddress(avail.locationSettings, initialSettings);
    });
    if (hasIncomplete) {
      this.errorMessage = CUSTOM_ADDRESS_ERROR;
      this.cdr.detectChanges();
      return false;
    }
    return true;
  }

  private _clearCustomAddressError() {
    if (this.errorMessage === CUSTOM_ADDRESS_ERROR) {
      this.errorMessage = '';
    }
  }

  private _setLocationSectionVisible(index: number, isVisible: boolean) {
    if (!this.locationViews[index]) {
      this._refreshLocationViews();
    }
    this.locationViews[index] = {
      ...this.locationViews[index],
      showSection: isVisible
    };
  }

  private _setSelectedServicePointPlace(index: number, point: AvailabilityLocationPoint, place: string) {
    if (point === 'departure') {
      this.selectedStartServicePointPlaces[index] = place;
      return;
    }
    this.selectedEndServicePointPlaces[index] = place;
  }

  private _refreshLocationViews() {
    const showSections = this.locationViews.length
      ? this.locationViews.map(view => view.showSection)
      : this.savedLocationSettingsForAvail.map(settings => !!settings);

    this.locationViews = buildAvailabilityLocationViews(
      this.availabilities,
      this.agentAddress,
      showSections,
      this.selectedStartServicePointPlaces,
      this.selectedEndServicePointPlaces
    );
  }

}
